/**
 * Product Image Optimizer - Fixed Version
 * 
 * KEY FIXES vs previous version:
 * 1. REMOVED removeSlickClones() — was causing last thumbnail to show first
 * 2. REMOVED fixSliders() / unslick+reinit — was causing heavy main thread blocking (INP)
 * 3. REMOVED all the repeated setTimeouts — reduces main thread tasks
 * 4. Hero image is fixed via HTML attribute (see note below), not JS
 * 5. Thumbnail lazy loading still works via IntersectionObserver (safe)
 */

class ProductImageOptimizer {
    constructor() {
        this.zoomInitialized = new Set();
        this.init();
    }

    init() {
        if (!document.querySelector('.productView')) return;
        this.fixHeroImageLCP();
        this.observeAndLoadThumbnails();
        this.lazyLoadZoom();
        this.cleanupEmptyElements();
    }

    /**
     * FIX #1: Hero image LCP
     * The main product image has loading="lazy" in the theme Liquid file.
     * We fix it here as a safety net, but the REAL fix is in your theme:
     * Change the hero img tag: loading="lazy" → loading="eager" fetchpriority="high"
     */
    fixHeroImageLCP() {
        const heroImg = document.querySelector(
            '.productView-nav .slick-current img, ' +
            '.productView-image img[data-main-image], ' +
            '.productView-nav .slick-slide:first-child img'
        );
        if (heroImg) {
            heroImg.loading = 'eager';
            heroImg.setAttribute('fetchpriority', 'high');
            heroImg.decoding = 'async';
            // If src is missing (edge case), restore from data-src
            if (heroImg.dataset.src && (!heroImg.src || heroImg.src.includes('blank'))) {
                heroImg.src = heroImg.dataset.src;
            }
        }
    }

    /**
     * FIX #2: Thumbnail loading via IntersectionObserver only
     * No Slick re-initialization. No clone removal. 
     * We just observe thumbnails and load them when visible.
     * Slick handles its own clones — we must NOT touch them.
     */
    observeAndLoadThumbnails() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadImage(entry.target);
                    observer.unobserve(entry.target);
                }
            });
        }, { rootMargin: '150px', threshold: 0.01 });

        // Observe all thumbnail images (real slides + clones - Slick manages clones itself)
        const thumbs = document.querySelectorAll(
            '.productView-for img, .productView-thumbnail img'
        );
        thumbs.forEach(img => {
            if (!img.dataset.loaded) {
                observer.observe(img);
            }
        });

        // Also observe non-active main slider images
        const mainSlideImgs = document.querySelectorAll(
            '.productView-nav .slick-slide:not(.slick-current) img'
        );
        mainSlideImgs.forEach(img => {
            if (!img.dataset.loaded) {
                observer.observe(img);
            }
        });
    }

    /**
     * Load a single image from its lazy data attributes
     */
    loadImage(img) {
        if (!img || img.dataset.loaded === 'true') return;

        const lazySrc = img.dataset.src || img.dataset.lazy || img.dataset.original;
        if (lazySrc && (!img.src || img.src.includes('blank') || img.src === '')) {
            img.src = lazySrc;
        }
        if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
        }

        img.removeAttribute('data-src');
        img.removeAttribute('data-lazy');
        img.removeAttribute('data-srcset');
        img.classList.remove('lazyload', 'lazyloading', 'slick-lazy');
        img.classList.add('lazyloaded');
        img.dataset.loaded = 'true';
    }

    /**
     * FIX #3: Lazy zoom — only initialize on hover, not on load
     * This avoids loading elevateZoom JS until the user actually needs it
     */
    lazyLoadZoom() {
        if (typeof $ === 'undefined') return;

        document.querySelectorAll('.productView-image').forEach(container => {
            container.addEventListener('mouseenter', () => {
                if (this.zoomInitialized.has(container)) return;
                if (typeof $.fn.elevateZoom === 'undefined') return;

                const $media = $(container).find('.media');
                if ($media.length) {
                    try {
                        $media.elevateZoom({
                            zoomType: 'inner',
                            cursor: 'crosshair',
                            zoomWindowFadeIn: 200,
                            zoomWindowFadeOut: 200,
                        });
                        this.zoomInitialized.add(container);
                    } catch (e) {}
                }
            }, { once: false });

            container.addEventListener('mouseleave', () => {
                const zc = document.querySelector('.zoomContainer');
                if (zc) zc.remove();
            });
        });
    }

    /**
     * FIX #4: Clean up only truly empty/broken elements
     * No longer removing display:none elements — those may be needed by apps
     */
    cleanupEmptyElements() {
        // Remove duplicate inline menus (safe)
        const menus = document.querySelectorAll('.header__inline-menu');
        if (menus.length > 1) {
            for (let i = 1; i < menus.length; i++) menus[i].remove();
        }

        // Remove truly broken images with no src at all
        document.querySelectorAll('img[src=""], img[src="()"]').forEach(img => {
            const parent = img.closest('.product-item, .card-media');
            if (parent) parent.remove();
            else img.remove();
        });
    }
}

// ============================================================================
// INITIALIZATION — runs as early as possible for LCP benefit
// ============================================================================
function initProductOptimizer() {
    if (document.querySelector('.productView')) {
        new ProductImageOptimizer();
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductOptimizer);
} else {
    initProductOptimizer();
}

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
        setTimeout(initProductOptimizer, 300);
    });
}

window.ProductImageOptimizer = ProductImageOptimizer;


/*
==============================================================================
IMPORTANT: THEME LIQUID FIX (do this too — JS alone is not enough for LCP)
==============================================================================

In your theme's product template Liquid file, find the hero product image tag.
It currently has:  loading="lazy"
Change it to:      loading="eager" fetchpriority="high"

It looks like this in the HTML output:
  <img ... loading="lazy" data-main-image="" ...>

Change to:
  <img ... loading="eager" fetchpriority="high" data-main-image="" ...>

This is the single most impactful fix for your LCP score. The JS fix above
is a fallback, but loading="lazy" in HTML fires before JS can change it.

==============================================================================
WHAT WAS REMOVED AND WHY
==============================================================================

❌ removeSlickClones()
   WHY REMOVED: Slick carousel REQUIRES .slick-cloned elements for infinite
   loop mode. Removing them breaks internal position calculations, which is
   exactly why your LAST thumbnail was appearing FIRST — the track transform
   was pointing to where a clone used to be.

❌ fixSliders() / unslick() + re-init
   WHY REMOVED: Destroying and rebuilding Slick creates a large synchronous
   JavaScript task on the main thread. This directly causes INP delays because
   the browser is busy re-calculating layouts when the user tries to interact.

❌ Multiple stacked setTimeouts (100ms, 200ms, 500ms, 1000ms, 2000ms)
   WHY REMOVED: These all run during the critical interaction window and
   compete with user input events, inflating INP measurement.

✅ KEPT: IntersectionObserver for thumbnail loading (performant, non-blocking)
✅ KEPT: Lazy zoom on mouseenter (defers heavy work until needed)
✅ KEPT: Hero image eager loading fix
✅ KEPT: Shopify design mode support
==============================================================================
*/